import React, { useState, useEffect } from 'react';
import { getCostEstimate, getVendorLocationDrivers, getVendorServiceRates, getClientServiceRates } from '../../services/vendorSearch.service';
import { X } from 'lucide-react';
import { formatMoney } from '../../utils/formatMoney';

const VendorDetailsModal = ({ isOpen, onClose, vendorLocationData, serviceDetails, routeDistances, onSelectVendor, isAssigning = false, hasFuelLocation }) => {
  const [costEstimate, setCostEstimate] = useState(null);
  const [costLoading, setCostLoading] = useState(false);
  const [costError, setCostError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);

  // Service rates state
  const [serviceRates, setServiceRates] = useState([]);
  const [serviceRatesLoading, setServiceRatesLoading] = useState(false);
  const [serviceRatesError, setServiceRatesError] = useState(null);
  const [selectedServiceRateId, setSelectedServiceRateId] = useState(null);
  const [showServiceRatesSelection, setShowServiceRatesSelection] = useState(false);

  const [clientRates, setClientRates] = useState([]);
  const [clientRatesLoading, setClientRatesLoading] = useState(false);
  const [clientRatesError, setClientRatesError] = useState(null);
  const [selectedClientRateId, setSelectedClientRateId] = useState(null);
  const [showClientRatesSelection, setShowClientRatesSelection] = useState(false);
  const [clientCostWarning, setClientCostWarning] = useState(null);

  // Fetch cost estimate when modal opens
  useEffect(() => {
    if (!isOpen || !vendorLocationData || !serviceDetails) {
      // Reset state when modal closes or data is not available
      setCostEstimate(null);
      setCostLoading(false);
      setCostError(null);
      setDrivers([]);
      setDriversLoading(false);
      setDriversError(null);
      setSelectedDriverId(null);
      setServiceRates([]);
      setServiceRatesLoading(false);
      setServiceRatesError(null);
      setSelectedServiceRateId(null);
      setShowServiceRatesSelection(false);
      setClientRates([]);
      setClientRatesLoading(false);
      setClientRatesError(null);
      setSelectedClientRateId(null);
      setShowClientRatesSelection(false);
      setClientCostWarning(null);
      return;
    }

    const clientSchemeId = serviceDetails?.service?.client_scheme?.id;
    const serviceTypeCode = serviceDetails.service?.scheme_service_type?.service_type_code;

    const fetchClientServiceRates = async () => {
      if (!clientSchemeId) {
        setClientRatesError('Client scheme not found for this service.');
        return;
      }

      setClientRatesLoading(true);
      setClientRatesError(null);

      try {
        const response = await getClientServiceRates(
          clientSchemeId,
          serviceTypeCode || undefined,
        );

        if (response.rates && response.rates.length > 0) {
          setClientRates(response.rates);
          setShowClientRatesSelection(true);
          setSelectedClientRateId(null);
        } else {
          setClientRatesError(
            'No client billable rates are configured for this scheme. Ask an admin to add client service rates before assigning a paid customer.',
          );
        }
      } catch (error) {
        console.error('Error fetching client service rates:', error);
        setClientRatesError(error.message || 'Failed to fetch client service rates');
      } finally {
        setClientRatesLoading(false);
      }
    };

    const fetchCostEstimate = async (clientServiceRatesId = null) => {
      setCostLoading(true);
      setCostError(null);
      setClientCostWarning(null);

      try {
        // Get service type IDs from service details
        const schemeServiceTypeId = serviceDetails.service?.scheme_service_type?.id;
        const schemeServiceSubtypeId = serviceDetails.service?.scheme_service_subtype?.id;
        const isPaidCustomer = serviceDetails?.customer?.is_paid_customer;

        console.log('Fetching cost estimate with:', {
          vendorLocationId: vendorLocationData.location.id,
          distanceKm: vendorLocationData.distance_km,
          schemeServiceTypeId,
          schemeServiceSubtypeId,
          isPaidCustomer,
          clientSchemeId,
          clientServiceRatesId,
        });

        const response = await getCostEstimate(
          vendorLocationData.location.id,
          vendorLocationData.distance_km,
          schemeServiceTypeId,
          schemeServiceSubtypeId,
          isPaidCustomer,
          {
            clientSchemeId,
            clientServiceRatesId,
          },
        );

        console.log('Cost estimate response:', response);

        setCostEstimate(response);
        setShowServiceRatesSelection(false);
        setSelectedServiceRateId(null);

        if (response?.estimated_client_cost != null) {
          setClientCostWarning(null);
          setShowClientRatesSelection(false);
          setSelectedClientRateId(response?.client_breakdown?.rate_id ?? null);
        } else if (response?.client_cost_warning) {
          setClientCostWarning(response.client_cost_warning);
          await fetchClientServiceRates();
        } else {
          setClientCostWarning(null);
          setShowClientRatesSelection(false);
          setSelectedClientRateId(null);
        }
      } catch (error) {
        console.error('Error fetching cost estimate:', error);
        console.error('Error status:', error.status);
        console.error('Error data:', error.errorData);

        // Check if this is a 404 error (no service rate found)
        if (error.status === 404) {
          console.log('Detected 404 error - fetching service rates');
          setCostError('No default service rate found for this service type. Please select a rate below.');
          // If cost estimation fails due to missing service rate, fetch available service rates for user selection
          await fetchServiceRates();
        } else {
          // For other errors, show the error message
          setCostError(error.message || 'Failed to fetch cost estimate');
        }
      } finally {
        setCostLoading(false);
      }
    };

    const fetchServiceRates = async () => {
      setServiceRatesLoading(true);
      setServiceRatesError(null);

      try {
        // Try to get vendor ID from different possible locations
        let vendorId = null;

        // First try: direct vendor ID
        if (vendorLocationData.location.vendor && vendorLocationData.location.vendor.id) {
          vendorId = vendorLocationData.location.vendor.id;
        }
        // Second try: vendor_id field (if it exists)
        else if (vendorLocationData.location.vendor_id) {
          vendorId = vendorLocationData.location.vendor_id;
        }
        // Third try: from vendor_code (we'd need to make another API call)
        else if (vendorLocationData.location.vendor && vendorLocationData.location.vendor.vendor_code) {
          // For now, we'll show an error, but in the future we could make an API call to get vendor by code
          throw new Error('Vendor ID not available. Please contact support.');
        }
        else {
          throw new Error('Vendor information not found in vendor location data');
        }

        console.log('Fetching service rates for vendor ID:', vendorId);

        if (!vendorId) {
          throw new Error('Vendor ID not found in vendor location data');
        }

        const response = await getVendorServiceRates(vendorId);

        console.log('Service rates response:', response);

        if (response.rates && response.rates.length > 0) {
          setServiceRates(response.rates);
          setShowServiceRatesSelection(true);
          // Don't auto-select - let user choose
          setSelectedServiceRateId(null);
        } else {
          setServiceRatesError('No service rates available for this vendor. Please contact support to configure rates.');
        }
      } catch (error) {
        console.error('Error fetching service rates:', error);
        console.error('Error status:', error.status);
        console.error('Error data:', error.errorData);

        // Check if this is a 404 error (vendor not found)
        if (error.status === 404) {
          setServiceRatesError('Vendor not found. Please contact support.');
        } else {
          setServiceRatesError(error.message || 'Failed to fetch service rates');
        }
      } finally {
        setServiceRatesLoading(false);
      }
    };

    const fetchDrivers = async () => {
      setDriversLoading(true);
      setDriversError(null);

      try {
        const response = await getVendorLocationDrivers(vendorLocationData.location.id);

        // Extract drivers from the nested structure
        const driversList = response.vendor_locations?.[0]?.drivers || [];
        setDrivers(driversList);
      } catch (error) {
        console.error('Error fetching drivers:', error);
        setDriversError(error.message || 'Failed to fetch drivers');
      } finally {
        setDriversLoading(false);
      }
    };

    fetchCostEstimate();
    fetchDrivers();
  }, [isOpen, vendorLocationData, serviceDetails]);

  // Early return if modal is not open or no vendor data
  if (!isOpen || !vendorLocationData) return null;

  const { location, distance_km, operating_hours, coordinates } = vendorLocationData;
  const { vendor, manager_user, location_address } = location;
  const serviceLocation = serviceDetails?.service?.pickup_location;
  const dropLocation = serviceDetails?.service?.drop_location;

  // Check if this is a towing service (has different drop location)
  const isTowingService = dropLocation && (
    dropLocation.latitude !== serviceLocation?.latitude ||
    dropLocation.longitude !== serviceLocation?.longitude ||
    dropLocation.address !== serviceLocation?.address
  );

  // Get route information for this vendor
  const vendorRouteInfo = routeDistances?.[location.id];

  // Calculate total distance - use route info if available, otherwise fallback to straight line distance
  const totalDistance = vendorRouteInfo?.isTowingService
    ? (vendorRouteInfo.outboundDistance + (vendorRouteInfo.serviceDistance || 0) + vendorRouteInfo.returnDistance + (vendorRouteInfo.fuelToPickupDistance || 0))
    : vendorRouteInfo?.totalDistance || distance_km;

  // Use API response for estimated amount
  const estimatedVendorAmount =
    costEstimate?.estimated_vendor_cost != null
      ? costEstimate.estimated_vendor_cost
      : (!serviceDetails?.customer?.is_paid_customer ? costEstimate?.estimated_cost : null);
  const estimatedClientAmount =
    costEstimate?.estimated_client_cost != null ? costEstimate.estimated_client_cost : null;
  const hasClientCost = estimatedClientAmount != null;
  const estimatedAmount = serviceDetails?.customer?.is_paid_customer
    ? (estimatedClientAmount || estimatedVendorAmount || 0)
    : (estimatedVendorAmount || costEstimate?.estimated_cost || 0);

  const isPaidCustomer = Boolean(serviceDetails?.customer?.is_paid_customer);
  const hasVendorCost = estimatedVendorAmount != null && Number(estimatedVendorAmount) > 0;
  const paidCustomerCanAssign = !isPaidCustomer || hasClientCost || hasVendorCost;
  const driverRequired = !isPaidCustomer;
  const paidCustomerNeedsClientRate = Boolean(
    isPaidCustomer &&
    showClientRatesSelection &&
    clientRates.length > 0 &&
    !selectedClientRateId &&
    !hasClientCost &&
    !hasVendorCost,
  );

  // Function to recalculate cost estimate with selected service rate
  const recalculateCostEstimate = async (serviceRateId) => {
    if (!serviceRateId) return;

    setCostLoading(true);
    setCostError(null);

    try {
      const selectedRate = serviceRates.find(rate => rate.id === serviceRateId);
      if (!selectedRate) {
        throw new Error('Selected service rate not found');
      }

      // Calculate cost using the selected service rate
      const baseCost = parseFloat(selectedRate.base_service_rate);
      const baseMaxKms = parseFloat(selectedRate.base_max_kms);
      const additionalRatePerKm = parseFloat(selectedRate.additional_rate_per_km);

      let calculatedCost = baseCost;
      if (totalDistance > baseMaxKms) {
        const additionalKms = totalDistance - baseMaxKms;
        calculatedCost += additionalKms * additionalRatePerKm;
      }

      // Create a mock cost estimate response
      const mockCostEstimate = {
        estimated_vendor_cost: calculatedCost,
        estimated_cost: isPaidCustomer ? estimatedClientAmount : calculatedCost,
        breakdown: {
          base_rate: baseCost,
          base_max_kms: baseMaxKms,
          additional_rate_per_km: additionalRatePerKm,
          total_distance: totalDistance,
          additional_kms: Math.max(0, totalDistance - baseMaxKms),
          additional_cost: Math.max(0, totalDistance - baseMaxKms) * additionalRatePerKm
        }
      };

      setCostEstimate(mockCostEstimate);
      setCostError(null);
    } catch (error) {
      console.error('Error recalculating cost estimate:', error);
      setCostError('Failed to recalculate cost estimate');
    } finally {
      setCostLoading(false);
    }
  };

  // Handle service rate selection change
  const handleServiceRateChange = (rateId) => {
    setSelectedServiceRateId(rateId);
    if (rateId) {
      recalculateCostEstimate(rateId);
    }
  };

  const handleClientRateChange = async (rateId) => {
    setSelectedClientRateId(rateId);
    if (!rateId || !vendorLocationData || !serviceDetails) return;

    setCostLoading(true);
    setClientCostWarning(null);
    try {
      const response = await getCostEstimate(
        vendorLocationData.location.id,
        vendorLocationData.distance_km,
        serviceDetails.service?.scheme_service_type?.id,
        serviceDetails.service?.scheme_service_subtype?.id,
        true,
        {
          clientSchemeId: serviceDetails?.service?.client_scheme?.id,
          clientServiceRatesId: rateId,
        },
      );
      setCostEstimate(response);
      if (response?.estimated_client_cost != null) {
        setClientCostWarning(null);
        setShowClientRatesSelection(false);
      } else {
        setClientCostWarning(response?.client_cost_warning || null);
      }
    } catch (error) {
      console.error('Error recalculating client cost:', error);
      setClientCostWarning(error.message || 'Failed to calculate customer cost');
    } finally {
      setCostLoading(false);
    }
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    const parts = [
      address.address,
      address.city,
      address.state,
      address.pincode
    ].filter(Boolean);
    return parts.join(', ');
  };

  const formatOperatingHours = (hours) => {
    if (!hours) return 'Not specified';

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return days.map((day, index) => {
      const daySchedule = hours[day];
      if (daySchedule && daySchedule.open && daySchedule.close) {
        return `${dayNames[index]}: ${daySchedule.open} - ${daySchedule.close}`;
      }
      return `${dayNames[index]}: Closed`;
    }).join('\n');
  };

  const handleSelectVendor = () => {
    if (onSelectVendor && !isAssigning) {
      onSelectVendor({
        vendorLocationId: location.id,
        vendorId: vendor.id,
        managerId: manager_user.id,
        driverId: selectedDriverId,
        estimatedCost: estimatedVendorAmount || estimatedAmount,
        estimatedClientCost: hasClientCost ? estimatedClientAmount : (hasVendorCost ? estimatedVendorAmount : null),
        estimatedTimeMinutes: vendorRouteInfo?.outboundDuration
          ? Math.round(vendorRouteInfo.outboundDuration + (vendorRouteInfo.serviceDuration || 0) + (vendorRouteInfo.fuelToPickupDuration || 0) + (vendorRouteInfo.fuelToServiceDuration || 0))
          : Math.round(totalDistance * 2), // Rough estimate if no route data
        distanceKm: totalDistance,
        vendorServiceRatesId: showServiceRatesSelection ? selectedServiceRateId : null,
        clientServiceRatesId: showClientRatesSelection ? selectedClientRateId : (costEstimate?.client_breakdown?.rate_id ?? null),
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-drk-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        {/* Header */}
        <div className="flex justify-between items-center py-4 px-6 border-b">
          <h2 className="text-lg font-semibold text-black">Vendor Details</h2>
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="bg-red-500 text-white rounded py-1 px-1  transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className='pl-6 pr-4 pt-4'>
          <div className="w-full max-h-[60vh] overflow-y-auto">
            {/* Vendor Information */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">Vendor Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Vendor Name</label>
                  <p className="text-gray-800 font-medium">{vendor.vendor_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Vendor Code</label>
                  <p className="text-gray-800 font-medium">{vendor.vendor_code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                  <p className="text-gray-800 font-medium">
                    <span className={`${vendor.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {vendor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Manager Information */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">Manager Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Manager Name</label>
                  <p className="text-gray-800 font-medium">{manager_user.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Phone Number</label>
                  <p className="text-gray-800 font-medium">{manager_user.mobile_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Username</label>
                  <p className="text-gray-800 font-medium">{manager_user.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                  <p className="text-gray-800 font-medium">
                    <span className={`${manager_user.is_active ? 'text-green-600' : 'text-red-600'}`}>
                      {manager_user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">Location Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Address</label>
                  <p className="text-gray-800">{formatAddress(location_address)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Coordinates</label>
                  <p className="text-gray-800">{coordinates.latitude}, {coordinates.longitude}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Distance from Service</label>
                  <p className="text-gray-800 font-medium">{distance_km} km</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Current Status</label>
                  <p className="text-gray-800 font-medium">
                    <span className={`${operating_hours.is_open ? 'text-green-600' : 'text-red-600'}`}>
                      {operating_hours.is_open ? 'Open' : 'Closed'}
                    </span>
                    <span className="text-gray-400 ml-1">
                      ({operating_hours.current_time} NZ)
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">Operating Hours</h3>
              <div className="bg-gray-50 p-3 rounded">
                <pre className="text-sm text-gray-700 whitespace-pre-line">
                  {formatOperatingHours(location.operating_hours)}
                </pre>
              </div>
            </div>

            {/* Service Details */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">
                Service Details {isTowingService && <span className="text-blue-600 text-sm">(Towing Service)</span>}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Service Number</label>
                  <p className="text-gray-800 font-medium">
                    {serviceDetails?.service?.service_number || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Service Type</label>
                  <p className="text-gray-800 font-medium">
                    {serviceDetails?.service?.scheme_service_type?.service_type_name || 'N/A'}
                  </p>
                </div>
                <div className={isTowingService ? '' : 'md:col-span-2'}>
                  <label className="block text-sm font-medium text-green-700 mb-1">Pickup Location</label>
                  <p className="text-gray-800">
                    {serviceLocation?.address || 'N/A'}
                  </p>
                </div>
                {isTowingService && (
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-1">Drop Location</label>
                    <p className="text-gray-800">
                      {dropLocation?.address || 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Cost Information */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">Cost Estimate</h3>

              {costLoading && (
                <div className="flex items-center justify-center py-4">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-600">Calculating cost estimate...</span>
                </div>
              )}

              {costError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-600 text-sm">{costError}</p>
                  <p className="text-red-500 text-xs mt-1">Using fallback calculation</p>
                </div>
              )}

              {clientCostWarning && !costError && !hasClientCost && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-amber-800 text-sm">{clientCostWarning}</p>
                  <p className="text-amber-700 text-xs mt-1">
                    Vendor cost is shown below. Select a client billable rate or ask an admin to configure client service rates for this scheme.
                  </p>
                </div>
              )}

              {/* Service Rates Selection - Show when cost estimation fails */}
              {showServiceRatesSelection && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-3">Select Service Rate (Required)</h4>
                  <p className="text-xs text-yellow-700 mb-3">
                    No default rate found for this service type and subtype combination. Please select an appropriate service rate from the available options below.
                  </p>

                  {serviceRatesLoading && (
                    <div className="flex items-center justify-center py-2">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-yellow-700 text-sm">Loading service rates...</span>
                    </div>
                  )}

                  {serviceRatesError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-red-600 text-sm">{serviceRatesError}</p>
                    </div>
                  )}

                  {!serviceRatesLoading && !serviceRatesError && serviceRates.length > 0 && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-yellow-800 mb-2">Available Service Rates *</label>
                        <select
                          value={selectedServiceRateId || ''}
                          onChange={(e) => handleServiceRateChange(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                          disabled={isAssigning}
                          required
                        >
                          <option value="">-- Select a service rate (Required) --</option>
                          {serviceRates.map((rate) => (
                            <option key={rate.id} value={rate.id}>
                              {rate.service_type_code}
                              {rate.service_subtype_code && ` / ${rate.service_subtype_code}`}
                              {` - ${formatMoney(rate.base_service_rate)} (${rate.base_max_kms}km base)`}
                              {rate.time_slot && ` - ${rate.time_slot}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedServiceRateId && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-sm text-green-800">
                            <strong>Selected Rate:</strong> {serviceRates.find(r => r.id === selectedServiceRateId)?.service_type_code}
                            {serviceRates.find(r => r.id === selectedServiceRateId)?.service_subtype_code &&
                              ` / ${serviceRates.find(r => r.id === selectedServiceRateId)?.service_subtype_code}`}
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            Base: {formatMoney(serviceRates.find(r => r.id === selectedServiceRateId)?.base_service_rate)}
                            for {serviceRates.find(r => r.id === selectedServiceRateId)?.base_max_kms}km
                          </p>
                        </div>
                      )}

                      {!selectedServiceRateId && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-800">
                            <strong>⚠️ Required:</strong> You must select a service rate to proceed with vendor assignment.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showClientRatesSelection && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-medium text-indigo-800 mb-3">Select Client Billable Rate (Required)</h4>
                  <p className="text-xs text-indigo-700 mb-3">
                    No automatic client rate matched this service. Choose the rate to bill the customer.
                  </p>

                  {clientRatesLoading && (
                    <div className="flex items-center justify-center py-2">
                      <span className="text-indigo-700 text-sm">Loading client service rates...</span>
                    </div>
                  )}

                  {clientRatesError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <p className="text-red-600 text-sm">{clientRatesError}</p>
                    </div>
                  )}

                  {!clientRatesLoading && !clientRatesError && clientRates.length > 0 && (
                    <div className="space-y-3">
                      <select
                        value={selectedClientRateId || ''}
                        onChange={(e) => handleClientRateChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                        className="w-full px-3 py-2 border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                        disabled={isAssigning}
                      >
                        <option value="">-- Select a client billable rate --</option>
                        {clientRates.map((rate) => (
                          <option key={rate.id} value={rate.id}>
                            {rate.service_type_code}
                            {rate.service_subtype_code && ` / ${rate.service_subtype_code}`}
                            {` - ${formatMoney(rate.base_service_rate)} (${rate.base_max_kms}km base)`}
                          </option>
                        ))}
                      </select>

                      {!selectedClientRateId && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-sm text-red-800">
                            <strong>Required:</strong> Select a client billable rate before assigning this paid customer.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-100 py-2 px-3 rounded-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div className="space-y-3">
                    {/* Vendor Cost Section */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Estimated Vendor Cost</label>
                      {costEstimate?.estimated_vendor_cost !== undefined ? (
                        <>
                          <p className="text-2xl font-bold text-green-600">{formatMoney(costEstimate.estimated_vendor_cost)}</p>
                          <p className="text-xs text-green-600 mt-1">Payable to Vendor</p>
                        </>
                      ) : costEstimate ? (
                        /* Fallback for old API or when only generic estimated_cost exists and it represents vendor cost (not paid customer) */
                        !serviceDetails?.customer?.is_paid_customer ? (
                          <>
                            <p className="text-2xl font-bold text-green-600">{formatMoney(estimatedAmount)}</p>
                            <p className="text-xs text-green-600 mt-1">Using vendor's default rate</p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-500">Not available</p>
                        )
                      ) : showServiceRatesSelection && selectedServiceRateId ? (
                        <>
                          <p className="text-2xl font-bold text-blue-600">{formatMoney(estimatedAmount)}</p>
                          <p className="text-xs text-blue-600 mt-1">Using selected service rate</p>
                        </>
                      ) : showServiceRatesSelection ? (
                        <>
                          <p className="text-lg font-medium text-yellow-600">Select a service rate</p>
                          <p className="text-xs text-yellow-600 mt-1">Cost will be calculated after selection</p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-medium text-red-600">No cost estimate available</p>
                        </>
                      )}
                    </div>

                    {/* Client Cost Section - Show ONLY if paid customer */}
                    {serviceDetails?.customer?.is_paid_customer && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Estimated Customer Cost</label>
                        {hasClientCost ? (
                          <>
                            <p className="text-2xl font-bold text-indigo-600">{formatMoney(estimatedClientAmount)}</p>
                            <p className="text-xs text-indigo-600 mt-1">Billable to Customer</p>
                          </>
                        ) : showClientRatesSelection ? (
                          <p className="text-sm text-amber-700">Select a client billable rate above</p>
                        ) : clientCostWarning ? (
                          <p className="text-sm text-amber-700">Configure client service rates in Admin</p>
                        ) : (
                          <p className="text-sm text-gray-500">Not available</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Distance</label>
                    <p className="text-gray-800 font-medium">{totalDistance.toFixed(1)} km</p>
                  </div>
                </div>

                {/* Detailed Route Breakdown for Towing Services */}
                {isTowingService && vendorRouteInfo && (
                  <div className="border-t pt-2 mt-2">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Route Breakdown</label>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      {hasFuelLocation && vendorRouteInfo.hasFuelLocation ? (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              Vendor → Fuel Station
                            </span>
                            <span className="font-medium">{vendorRouteInfo.outboundDistance?.toFixed(1)} km</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                              Fuel Station → Pickup
                            </span>
                            <span className="font-medium">
                              {vendorRouteInfo.fuelToPickupDistance ? vendorRouteInfo.fuelToPickupDistance.toFixed(1) : '0.0'} km
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                              Pickup → Drop
                            </span>
                            <span className="font-medium">{vendorRouteInfo.serviceDistance?.toFixed(1)} km</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                              Drop → Vendor
                            </span>
                            <span className="font-medium">{vendorRouteInfo.returnDistance?.toFixed(1)} km</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              Vendor → Pickup
                            </span>
                            <span className="font-medium">{vendorRouteInfo.outboundDistance?.toFixed(1)} km</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                              Pickup → Drop
                            </span>
                            <span className="font-medium">{vendorRouteInfo.serviceDistance?.toFixed(1)} km</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                              Drop → Vendor
                            </span>
                            <span className="font-medium">{vendorRouteInfo.returnDistance?.toFixed(1)} km</span>
                          </div>
                        </>
                      )}
                      {vendorRouteInfo.outboundDuration && (
                        <div className="flex justify-between items-center border-t pt-1 mt-1">
                          <span className="text-gray-600">Estimated Travel Time</span>
                          <span className="font-medium">~{Math.round(vendorRouteInfo.outboundDuration + (vendorRouteInfo.serviceDuration || 0) + (vendorRouteInfo.fuelToPickupDuration || 0) + (vendorRouteInfo.fuelToServiceDuration || 0))} min</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Two-point route info for regular services */}
                {!isTowingService && vendorRouteInfo && (
                  <div className="border-t pt-2 mt-2">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Route Breakdown</label>
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      {hasFuelLocation && vendorRouteInfo.hasFuelLocation ? (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              Vendor → Fuel Station
                            </span>
                            <span className="font-medium">{vendorRouteInfo.outboundDistance?.toFixed(1)} km</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                              Fuel Station → Service
                            </span>
                            <span className="font-medium">
                              {vendorRouteInfo.fuelToServiceDistance ? vendorRouteInfo.fuelToServiceDistance.toFixed(1) : '0.0'} km
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                              Service → Vendor
                            </span>
                            <span className="font-medium">{vendorRouteInfo.returnDistance?.toFixed(1)} km</span>
                          </div>
                          {vendorRouteInfo.outboundDuration && (
                            <div className="flex justify-between items-center border-t pt-1 mt-1">
                              <span className="text-gray-600">Estimated Travel Time</span>
                              <span className="font-medium">~{Math.round(vendorRouteInfo.outboundDuration + (vendorRouteInfo.fuelToServiceDuration || 0))} min</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                              Vendor → Service
                            </span>
                            <span className="font-medium">{vendorRouteInfo.outboundDistance?.toFixed(1)} km</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="flex items-center">
                              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                              Service → Vendor
                            </span>
                            <span className="font-medium">{vendorRouteInfo.returnDistance?.toFixed(1)} km</span>
                          </div>
                          {vendorRouteInfo.outboundDuration && (
                            <div className="flex justify-between items-center border-t pt-1 mt-1">
                              <span className="text-gray-600">Estimated Travel Time</span>
                              <span className="font-medium">~{Math.round(vendorRouteInfo.outboundDuration)} min</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Driver Selection */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h3 className="text-x1 font-semibold text-gray-800 mb-3 bg-gray-100 rounded-md py-1.5 px-3">Driver Assignment</h3>

              {driversLoading && (
                <div className="flex items-center justify-center py-4">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-gray-600">Loading drivers...</span>
                </div>
              )}

              {driversError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-600 text-sm">{driversError}</p>
                </div>
              )}

              {!driversLoading && !driversError && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Select Driver</label>
                    <select
                      value={selectedDriverId || ''}
                      onChange={(e) => setSelectedDriverId(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={isAssigning}
                    >
                      <option value="">-- Select a driver --</option>
                      {drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name} ({driver.mobile_number})
                          {driver.current_service && ` - Busy with Service #${driver.current_service.service_number}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {drivers.length === 0 && (
                    <div className="text-center text-gray-500 py-4">
                      No drivers available for this vendor location
                    </div>
                  )}

                  {selectedDriverId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Selected Driver:</strong> {drivers.find(d => d.id === selectedDriverId)?.name}
                      </p>
                    </div>
                  )}

                  {!selectedDriverId && drivers.length > 0 && driverRequired && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Note:</strong> Please select a driver to assign this service.
                      </p>
                    </div>
                  )}

                  {!selectedDriverId && drivers.length > 0 && isPaidCustomer && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>Paid customer:</strong> Driver selection is optional now. The driver can be assigned after the customer pays.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Footer */}
        <div className="flex justify-center space-x-4 py-5 px-6 border-t">
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSelectVendor}
            disabled={
              isAssigning ||
              (driverRequired && !selectedDriverId) ||
              (showServiceRatesSelection && !selectedServiceRateId && !isPaidCustomer) ||
              paidCustomerNeedsClientRate ||
              (isPaidCustomer && !paidCustomerCanAssign)
            }
            className={`px-6 py-2 rounded-lg text-white font-medium ${
              !isAssigning &&
              (!driverRequired || selectedDriverId) &&
              (!showServiceRatesSelection || selectedServiceRateId || isPaidCustomer) &&
              !paidCustomerNeedsClientRate &&
              paidCustomerCanAssign
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-400 cursor-not-allowed'
              } disabled:opacity-50`}
          >
            {isAssigning ? 'Assigning...' : operating_hours?.is_open === false ? 'Assign Vendor (Closed)' : 'Assign Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailsModal; 